/**
 * ReviewDuplicatesPage — /operators/duplicates (lx-coord/operator-dedupe-plan.md; Corbin
 * 2026-10-20: "how do I know what to merge, without asking you?").
 *
 * One card per suspected duplicate group, its rows side by side (a column each): name, display
 * name, abbreviation, states, regulator id, active, wells, haulers by name, portal companies, and a
 * verdict per row against the suggested survivor (badged, with its reason). "Merge into …" opens
 * the shared MergeDialog with that survivor pre-selected as KEEP. Display names shared across
 * different legal companies are listed first. Nothing here changes data until Confirm.
 *
 * "Not a duplicate…" (0247) rules a row separate from the group's top row, with an optional
 * reason; the row leaves the group (and the list chip). The rulings are listed at the bottom,
 * collapsed, each with Undo.
 */
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { ApiError } from '@lodex-pro/web-shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Pill, fieldStyle, subText } from '../../ui/bits';
import { useBackTo } from '../../lib/url-state';
import {
  useDismissDuplicate,
  useOperatorDuplicates,
  useUndoDismissal,
  type DuplicateDismissal,
  type DuplicateGroup,
  type DuplicateMember,
  type VerdictKind,
} from './dedupe-hooks';
import { MergeDialog, type MergeCandidate } from './MergeDialog';

const VERDICT_LABEL: Record<
  VerdictKind,
  { label: string; tone: 'positive' | 'accent' | 'warning' | 'muted' }
> = {
  MERGE: { label: 'Same company', tone: 'positive' },
  MERGE_OTHER_STATE: { label: 'Same company, other state', tone: 'positive' },
  MERGE_EMPTY: { label: 'Empty row', tone: 'positive' },
  SAME_BRAND: { label: 'Same brand', tone: 'accent' },
  SAME_FIRST_WORD: { label: 'Same first word only', tone: 'muted' },
  BOTH_IN_USE: { label: 'Both in use', tone: 'warning' },
  SAME_CONTACT: { label: 'Same phone or address', tone: 'muted' },
};

const candidate = (m: DuplicateMember): MergeCandidate => ({
  id: m.id,
  name: m.name,
  state: m.state,
});

interface Opened {
  readonly pair: readonly [MergeCandidate, MergeCandidate];
  readonly suggestedKeepId: string | null;
}

/** The pair a "Not a duplicate…" is about: the group's top row and the row being ruled. */
interface Ruling {
  readonly top: DuplicateMember;
  readonly other: DuplicateMember;
}

const label = (o: { state: string | null; name: string }): string => `${o.state ?? '—'} ${o.name}`;

/** "Not a duplicate…": an optional reason, then the ruling. */
function NotDuplicateDialog({
  ruling,
  onClose,
}: {
  ruling: Ruling | null;
  onClose: () => void;
}): React.JSX.Element {
  const dismiss = useDismissDuplicate();
  const [reason, setReason] = React.useState('');
  React.useEffect(() => setReason(''), [ruling?.other.id]);
  const confirm = (): void => {
    if (ruling === null) return;
    const why = reason.trim();
    dismiss.mutate(
      {
        operatorIds: [ruling.top.id, ruling.other.id],
        ...(why !== '' ? { reason: why } : {}),
      },
      {
        onSuccess: () => {
          toast.success(`${label(ruling.other)} is no longer suggested with ${label(ruling.top)}`);
          onClose();
        },
        onError: (err) => {
          const already =
            err instanceof ApiError && err.code === 'OPERATOR_DUPLICATE_ALREADY_DISMISSED';
          const mergedAway =
            err instanceof ApiError && err.fields.some((x) => x.reason === 'merged_away');
          toast.error(
            already
              ? 'That pair is already ruled not a duplicate.'
              : mergedAway
                ? 'One of these rows was merged away. Reload the page.'
                : 'Could not save the ruling',
          );
        },
      },
    );
  };
  return (
    <Dialog open={ruling !== null} onOpenChange={(o): void => (o ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Not a duplicate?</DialogTitle>
        </DialogHeader>
        {ruling !== null && (
          <p style={{ fontSize: '14px' }}>
            <strong>{label(ruling.other)}</strong> will no longer be suggested as a duplicate of{' '}
            <strong>{label(ruling.top)}</strong>. You can undo this at the bottom of this page.
          </p>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '14px' }}>
          Why (optional)
          <textarea
            aria-label="Why they are different"
            value={reason}
            maxLength={500}
            onChange={(e): void => setReason(e.target.value)}
            style={{ ...fieldStyle, minHeight: '64px' }}
          />
        </label>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={dismiss.isPending} onClick={confirm}>
            {dismiss.isPending ? 'Saving…' : 'Not a duplicate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One ruling in the Undo section. */
function RulingRow({ d }: { d: DuplicateDismissal }): React.JSX.Element {
  const undo = useUndoDismissal(d.id);
  return (
    <li style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '4px 0' }}>
      <span style={{ fontSize: '14px' }}>
        {label(d.operatorA)} ≠ {label(d.operatorB)}
        {d.reason !== null ? ` — ${d.reason}` : ''}
        <span style={{ ...subText, textTransform: 'none' }}> · {d.dismissedAt.slice(0, 10)}</span>
      </span>
      <Button
        type="button"
        variant="outline"
        disabled={undo.isPending}
        onClick={(): void =>
          undo.mutate(
            {},
            {
              onSuccess: () => toast.success('Ruling undone'),
              onError: () => toast.error('Could not undo the ruling'),
            },
          )
        }
      >
        Undo
      </Button>
    </li>
  );
}

export function ReviewDuplicatesPage(): React.JSX.Element {
  const navigate = useNavigate();
  const backTo = useBackTo('/operators');
  const { data, isLoading, isError } = useOperatorDuplicates();
  const [opened, setOpened] = React.useState<Opened | null>(null);
  const [ruling, setRuling] = React.useState<Ruling | null>(null);

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <button
        type="button"
        onClick={(): void => void navigate(backTo)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: 'none',
          border: 'none',
          color: 'var(--color-text-secondary)',
          cursor: 'pointer',
          fontSize: '13px',
          marginBottom: '12px',
          padding: 0,
        }}
      >
        <ArrowLeft size={16} aria-hidden="true" /> Operators
      </button>
      <h2 style={{ fontSize: '22px', fontWeight: 700, margin: '0 0 4px' }}>Review duplicates</h2>
      <p style={{ ...subText, textTransform: 'none', marginBottom: '16px' }}>
        Rows that look like the same company or the same brand. Each merge shows its exact effect
        before you confirm.
      </p>

      {isLoading && <p style={subText}>Loading…</p>}
      {isError && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Could not load the duplicate review.
        </p>
      )}

      {data !== undefined && (
        <>
          {data.displayNameCollisions.length > 0 && (
            <section
              aria-label="Display-name collisions"
              style={{
                border: '1px solid var(--color-warning)',
                borderRadius: '12px',
                padding: '12px 16px',
                marginBottom: '16px',
              }}
            >
              <h3 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 6px' }}>
                Display names shared across different legal companies
              </h3>
              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '14px' }}>
                {data.displayNameCollisions.map((c) => (
                  <li key={c.shownCore}>{c.text}</li>
                ))}
              </ul>
            </section>
          )}

          {data.groups.length === 0 ? (
            <p style={{ ...subText, textTransform: 'none' }}>No suspected duplicates.</p>
          ) : (
            data.groups.map((g) => (
              <GroupCard
                key={g.members.map((m) => m.id).join(':')}
                group={g}
                onMerge={(pair, suggestedKeepId): void => setOpened({ pair, suggestedKeepId })}
                onNotDuplicate={(top, other): void => setRuling({ top, other })}
              />
            ))
          )}

          {data.dismissals.length > 0 && (
            <details style={{ marginTop: '8px' }}>
              <summary style={{ cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>
                Ruled not duplicates ({data.dismissals.length})
              </summary>
              <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
                {data.dismissals.map((d) => (
                  <RulingRow key={d.id} d={d} />
                ))}
              </ul>
            </details>
          )}
        </>
      )}

      <NotDuplicateDialog ruling={ruling} onClose={(): void => setRuling(null)} />

      <MergeDialog
        pair={opened?.pair ?? null}
        suggestedKeepId={opened?.suggestedKeepId ?? null}
        onOpenChange={(): void => setOpened(null)}
      />
    </div>
  );
}

function GroupCard({
  group,
  onMerge,
  onNotDuplicate,
}: {
  group: DuplicateGroup;
  onMerge: (
    pair: readonly [MergeCandidate, MergeCandidate],
    suggestedKeepId: string | null,
  ) => void;
  onNotDuplicate: (top: DuplicateMember, other: DuplicateMember) => void;
}): React.JSX.Element {
  const survivor = group.members.find((m) => m.id === group.survivorId) ?? null;
  // A ruling is against the group's top row: the suggested survivor, else the first row.
  const top = survivor ?? group.members[0] ?? null;
  const th: React.CSSProperties = {
    textAlign: 'left',
    fontWeight: 500,
    color: 'var(--color-text-secondary)',
    padding: '6px 8px',
    whiteSpace: 'nowrap',
    verticalAlign: 'top',
  };
  const td: React.CSSProperties = {
    padding: '6px 8px',
    borderLeft: '1px solid var(--color-border-secondary)',
    verticalAlign: 'top',
    minWidth: '180px',
    overflowWrap: 'anywhere',
  };
  const row = (
    label: string,
    render: (m: DuplicateMember) => React.ReactNode,
  ): React.JSX.Element => (
    <tr style={{ borderTop: '1px solid var(--color-border-secondary)' }}>
      <th scope="row" style={th}>
        {label}
      </th>
      {group.members.map((m) => (
        <td key={m.id} style={td}>
          {render(m)}
        </td>
      ))}
    </tr>
  );

  return (
    <section
      aria-label={`Duplicate group ${group.key}`}
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderRadius: 'var(--radius-card)',
        padding: '16px',
        marginBottom: '16px',
      }}
    >
      <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0 }}>
        {group.key} ({group.members.length})
      </h3>
      <p style={{ fontSize: '13px', margin: '4px 0 10px' }}>{group.survivorReason}</p>
      {group.omittedSameFirstWord > 0 && (
        <p style={{ ...subText, textTransform: 'none', margin: '-6px 0 10px' }}>
          {group.omittedSameFirstWord} other rows share only the first word{' '}
          {group.key.split(' ')[0]} and are not shown.
        </p>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: '13px', width: '100%' }}>
          <tbody>
            {row('Name', (m) => (
              <>
                <Link to={`/operators/${m.id}`} style={{ fontWeight: 600 }}>
                  {m.name}
                </Link>
                {m.isSuggestedSurvivor && (
                  <div style={{ marginTop: '4px' }}>
                    <Pill label="Suggested keep" tone="positive" />
                  </div>
                )}
              </>
            ))}
            {row('Display name', (m) => m.displayName ?? '—')}
            {row('Abbreviation', (m) => m.abbreviation ?? '—')}
            {row('States', (m) => (m.states.length > 0 ? m.states.join(', ') : (m.state ?? '—')))}
            {row('Regulator id', (m) =>
              m.regulatorOperatorId !== null
                ? `${m.regulator ?? ''} ${m.regulatorOperatorId}`
                : '—',
            )}
            {row('Active', (m) => (m.isActive ? 'Yes' : 'No'))}
            {row('Wells', (m) => m.wellCount.toLocaleString('en-US'))}
            {row('Haulers', (m) => (m.haulers.length > 0 ? m.haulers.join(', ') : '—'))}
            {/* OPEN-168: from the RRC P-5 file (Texas rows); — when unknown. */}
            {row('Address', (m) => m.contact?.address ?? '—')}
            {row('Phone', (m) =>
              m.contact !== null && m.contact !== undefined && m.contact.phones.length > 0
                ? m.contact.phones.join(', ')
                : '—',
            )}
            {row('Portal companies', (m) => m.portalCompanyCount)}
            {row('Verdict', (m) =>
              m.verdict === null ? (
                '—'
              ) : (
                <>
                  <Pill
                    label={VERDICT_LABEL[m.verdict.kind].label}
                    tone={VERDICT_LABEL[m.verdict.kind].tone}
                  />
                  <div style={{ marginTop: '4px' }}>{m.verdict.text}</div>
                  {m.verdict.notes.map((n) => (
                    <div key={n} style={{ ...subText, textTransform: 'none', marginTop: '2px' }}>
                      {n}
                    </div>
                  ))}
                </>
              ),
            )}
            {row('', (m) =>
              survivor !== null ? (
                m.id === survivor.id ? null : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={(): void => onMerge([candidate(survivor), candidate(m)], survivor.id)}
                    style={{ whiteSpace: 'normal', textAlign: 'left' }}
                  >
                    Merge into {survivor.state ?? '—'} {survivor.displayName ?? survivor.name}…
                  </Button>
                )
              ) : (
                // Both in use: no survivor is pre-picked; the dialog makes him choose.
                group.members
                  .filter((o) => o.id !== m.id)
                  .slice(0, 1)
                  .map((o) => (
                    <Button
                      key={o.id}
                      type="button"
                      variant="outline"
                      onClick={(): void => onMerge([candidate(o), candidate(m)], null)}
                    >
                      Merge with {o.state ?? '—'} {o.name}…
                    </Button>
                  ))
              ),
            )}
            {row('', (m) =>
              top === null || m.id === top.id ? null : (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={(): void => onNotDuplicate(top, m)}
                  style={{ whiteSpace: 'normal', textAlign: 'left' }}
                >
                  Not a duplicate…
                </Button>
              ),
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
